<template>
  <UApp :toaster="{ position: 'top-right', class: 'e2e-toaster' }">
    <MaintenanceBoard
      v-if="displayMode !== 'app'"
      variant="full"
      :reconnected="displayMode === 'reconnected'"
      :title="
        displayMode === 'reconnected'
          ? $t('maintenance.reconnected_title')
          : $t('maintenance.board_title')
      "
      :description="
        displayMode === 'reconnected'
          ? ''
          : appMessage || $t('maintenance.board_description')
      "
      :eta="
        displayMode === 'reconnected' || !appEta
          ? ''
          : $t('maintenance.eta_label', { eta: formatMaintenanceEta(appEta) })
      "
      :retry-label="
        displayMode === 'reconnected' ? '' : $t('maintenance.retry_button')
      "
      :auto-check-hint="
        displayMode === 'reconnected'
          ? ''
          : $t('maintenance.auto_check_hint', {
              seconds: secondsUntilNextPoll,
            })
      "
      @retry="refreshMaintenanceStatus"
    />
    <template v-else>
      <a href="#main-content" class="skip-link">
        {{ $t('accessibility.skip_to_content') }}
      </a>
      <div id="app" class="app-container">
        <MobileMenu
          v-if="isMobileView"
          :logo-url="logoUrl"
          @menu-opened="handleMobileMenuOpened"
          @menu-closed="handleMobileMenuClosed"
        />
        <SideMenu v-if="!isMobileView" :logo-url="logoUrl" />
        <div id="main-content" class="page-content">
          <NuxtLoadingIndicator />
          <div class="flex w-full flex-1 flex-col items-center">
            <MaintenanceBanner
              v-if="isAppDegraded"
              class="w-full"
              :description="appMessage || $t('maintenance.banner_description')"
            />
            <NuxtPage
              class="min-h-0 w-full flex-1"
              :is-mobile-view="isMobileView"
            />
          </div>
        </div>
      </div>
      <FooterElement
        v-if="isMobileView"
        :hovered="true"
        class="mobile-footer"
      />
    </template>
  </UApp>
</template>

<script>
import { computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import SideMenu from '~/components/MainPage/SideMenu.vue'
import MobileMenu from '~/components/MainPage/MobileMenu.vue'
import FooterElement from '~/components/Utilities/FooterElement.vue'
import MaintenanceBanner from '~/components/Utilities/maintenance/MaintenanceBanner.vue'
import MaintenanceBoard from '~/components/Utilities/maintenance/MaintenanceBoard.vue'
import { useMaintenanceStatus } from '~/composables/useMaintenanceStatus'
import { useMaintenanceReveal } from '~/composables/useMaintenanceReveal'
import { formatMaintenanceEta } from '~/utils/maintenance'

export default {
  components: {
    SideMenu,
    MobileMenu,
    FooterElement,
    MaintenanceBanner,
    MaintenanceBoard,
  },
  setup() {
    const { locale } = useI18n()
    const runtimeConfig = useRuntimeConfig()
    const publicConfig = runtimeConfig.public
    const runtimeTitle = computed(() =>
      (publicConfig.instanceVendor || 'laniqo').toUpperCase()
    )

    useHead({
      title: runtimeTitle,
      htmlAttrs: {
        lang: locale,
      },
    })

    const {
      isAppDegraded,
      isAppDisabled,
      appMessage,
      appEta,
      secondsUntilNextPoll,
      refreshMaintenanceStatus,
      startPolling,
      stopPolling,
    } = useMaintenanceStatus()
    const { displayMode } = useMaintenanceReveal(isAppDisabled)

    onMounted(startPolling)
    onBeforeUnmount(stopPolling)

    return {
      publicConfig,
      isAppDegraded,
      displayMode,
      appMessage,
      appEta,
      secondsUntilNextPoll,
      refreshMaintenanceStatus,
      formatMaintenanceEta,
    }
  },
  data() {
    return {
      isMobileView: false,
      mobileBreakpoint: 1040,
      fullViewportHeight: 0,
      jiraWidgetVisible: false,
      faviconUrl: null,
      logoUrl: null,
    }
  },
  watch: {},
  async mounted() {
    this.checkViewport()
    window.addEventListener('resize', this.checkViewport)

    const clientId = this.$keycloak?.clientId
    if (!clientId) {
      console.error('Keycloak client ID not found')
      return
    }

    const logos = await getLogos(clientId)
    const logo = await downloadLogoGoogle(logos.logo_url)
    const favicon = await downloadLogoGoogle(logos.favicon_url)

    // Replace favicon dynamically
    if (favicon) {
      this.faviconUrl = URL.createObjectURL(favicon)
      this.updateFavicon(this.faviconUrl, favicon.type)
    }

    // Store logo URL for child components
    if (logo) {
      this.logoUrl = URL.createObjectURL(logo)
    }

    if (this.publicConfig.instanceVendor === 'pons' && !this.isMobileView) {
      this.runJiraScript()
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkViewport)
    // Clean up object URLs if they exist
    if (this.faviconUrl) {
      URL.revokeObjectURL(this.faviconUrl)
    }
    if (this.logoUrl) {
      URL.revokeObjectURL(this.logoUrl)
    }
  },
  methods: {
    updateFavicon(url, type = 'image/x-icon') {
      // Remove existing favicon links
      const existingLinks = document.querySelectorAll("link[rel*='icon']")
      existingLinks.forEach((link) => link.remove())

      // Create new favicon link
      const link = document.createElement('link')
      link.rel = 'icon'
      link.type = type || 'image/x-icon'
      link.href = url
      document.head.appendChild(link)
    },
    checkViewport() {
      this.isMobileView = window.innerWidth <= this.mobileBreakpoint
    },

    handleMobileMenuOpened() {
      if (this.publicConfig.instanceVendor === 'pons') {
        this.runJiraScript()
      }
    },

    handleMobileMenuClosed() {
      if (this.publicConfig.instanceVendor === 'pons') {
        this.hideJiraWidget()
      }
    },

    showJiraWidget() {
      // Show Jira widget elements
      const jiraElements = document.querySelectorAll(
        '[data-jsd-embedded], #jsd-widget, .jsd-widget'
      )
      jiraElements.forEach((element) => {
        element.style.display = 'block'
      })
      this.jiraWidgetVisible = true
    },

    hideJiraWidget() {
      // Hide Jira widget elements
      const jiraElements = document.querySelectorAll(
        '[data-jsd-embedded], #jsd-widget, .jsd-widget'
      )
      jiraElements.forEach((element) => {
        element.style.display = 'none'
      })
      this.jiraWidgetVisible = false
    },

    runJiraScript() {
      // If script is already loaded, just show the widget
      if (document.querySelector('script[data-jsd-embedded]')) {
        this.showJiraWidget()
        return
      }

      const jhdScript = document.createElement('script')
      jhdScript.type = 'text/javascript'
      jhdScript.setAttribute('data-jsd-embedded', '')
      jhdScript.setAttribute('data-key', 'b08820bc-bdb2-4e67-9b55-55a86516e589')
      jhdScript.setAttribute(
        'data-base-url',
        'https://jsd-widget.atlassian.com'
      )
      jhdScript.src = 'https://jsd-widget.atlassian.com/assets/embed.js'
      if (jhdScript.readyState) {
        jhdScript.onreadystatechange = () => {
          if (
            jhdScript.readyState === 'loaded' ||
            jhdScript.readyState === 'complete'
          ) {
            jhdScript.onreadystatechange = null
            this.onScriptLoaded()
          }
        }
      } else {
        jhdScript.onload = this.onScriptLoaded
      }
      document.getElementsByTagName('head')[0].appendChild(jhdScript)
    },
    onScriptLoaded() {
      let event
      if (typeof Event === 'function') {
        event = new Event('DOMContentLoaded', {
          bubbles: true,
          cancelable: true,
        })
      } else {
        event = document.createEvent('Event')
        event.initEvent('DOMContentLoaded', true, true)
      }
      document.dispatchEvent(event)

      // Show the widget after the script is loaded and DOM event is dispatched
      setTimeout(() => {
        this.showJiraWidget()
      }, 100)
    },
  },
}
</script>

<style>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.page-content {
  margin-left: 5rem;
  flex-grow: 1;
  display: flex;
  justify-content: center;
}

@media (max-width: 1040px) {
  .page-content {
    margin-left: 0;
  }

  .mobile-footer {
    margin-top: auto;
    width: 100%;
  }

  .app-container {
    display: flex;
    flex-direction: column;
  }
}
</style>
